import React, { useEffect, useMemo, useState } from 'react';
import { 
  FileSpreadsheet, Edit2, Check, X, Plus, Trash2, Upload, 
  ArrowRightLeft, MoveRight, RefreshCw 
} from 'lucide-react';
import { crmDataService } from '../services/crmDataService';
import * as XLSX from 'xlsx';
import PermissionGate from './PermissionGate'; // 🚨 [유지] 마법 상자 불러오기

// 데이터 타입 정의 (DB 구조 반영)
interface TargetRow {
  id: number;
  user_id: number | null; 
  manager: string;
  type: string;
  months: number[];
  total: number;
}
interface UserOption {
  id: number;
  name: string;
  teamName: string;
}

interface TeamOption {
  id: number;
  name: string;
}

// 🚨 [핵심] App.tsx에서 보내주는 currentUser를 받기 위한 인터페이스
interface SalesTargetProps {
  currentUser?: any;
  globalRefreshKey?: number;
}

// 🚨 [핵심] React.FC<SalesTargetProps>로 타입을 지정하고 인자에서 { currentUser }를 꺼냅니다.
const SalesTarget: React.FC<SalesTargetProps> = ({ currentUser, globalRefreshKey = 0 }) => {
  const [rows, setRows] = useState<TargetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editMonths, setEditMonths] = useState<Record<number, number[]>>({});
  const [savingId, setSavingId] = useState<number | null>(null);

  const [allUsers, setAllUsers] = useState<UserOption[]>([]);
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedTeamName, setSelectedTeamName] = useState('');
  const [selectedManagerName, setSelectedManagerName] = useState('');
  const [addingManager, setAddingManager] = useState(false);
  const [deletingManager, setDeletingManager] = useState<string | null>(null);

  // ✨ [추가] 목표 이전(Transfer) 관련 상태값
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [transferFrom, setTransferFrom] = useState(''); // 보내는 담당자
  const [transferTo, setTransferTo] = useState('');     // 받는 담당자
  const [isTransferring, setIsTransferring] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState<number[]>([]); 
  // 💰 [정밀수정] 제품/상품 개별 입력을 위한 상태 구조 { 월인덱스: { prod: 금액, item: 금액 } }
  const [transferAmounts, setTransferAmounts] = useState<Record<number, { prod: number; item: number }>>({});

  // 1. 데이터 불러오기 (자동 동기화 로직이 백엔드에 있으므로 단순화)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        const json = await crmDataService.getSalesTargets();

        if (json.status === 'success') {
          setRows(json.data);
        } else {
          setError(json.message || '데이터를 불러오지 못했습니다.');
        }
      } catch (err) {
        setError('서버 연결에 실패했습니다.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [reloadKey, globalRefreshKey]); // globalRefreshKey가 변경될 때마다 데이터 새로고침

  useEffect(() => {
    const fetchOptions = async () => {
      const [usersRaw, teamsRaw] = await Promise.all([
        crmDataService.getAllUsers(),
        crmDataService.getActiveTeams()
      ]);

      const users: UserOption[] = (usersRaw || [])
        .map((u: any) => ({
          id: Number(u.id),
          name: String(u.name || '').trim(),
          teamName: String(u.team_name || '미배정').trim() || '미배정'
        }))
        .filter((u: UserOption) => Number.isFinite(u.id) && !!u.name);

      const teamList: TeamOption[] = (teamsRaw || [])
        .map((t: any) => ({
          id: Number(t.id),
          name: String(t.name || '').trim()
        }))
        .filter((t: TeamOption) => !!t.name);

      setAllUsers(users);
      setTeams(teamList);
    };

    fetchOptions();
  }, []);

  const groupedRows = useMemo(() => {
    return Object.entries(
      rows.reduce((acc, row) => {
        if (!acc[row.manager]) acc[row.manager] = [];
        acc[row.manager].push(row);
        return acc;
      }, {} as Record<string, TargetRow[]>)
    );
  }, [rows]);

  const existingManagerNames = useMemo(() => {
    return new Set(rows.map((row) => row.manager));
  }, [rows]);

  const teamNames = useMemo(() => {
    const fromTeams = teams.map((t) => t.name);
    const fromUsers = allUsers.map((u) => u.teamName);
    return Array.from(new Set([...fromTeams, ...fromUsers])).filter(Boolean);
  }, [allUsers, teams]);

  const availableManagersByTeam = useMemo(() => {
    if (!selectedTeamName) return [];
    const names = allUsers
      .filter((user) => user.teamName === selectedTeamName)
      .map((user) => user.name);
    return Array.from(new Set(names)).filter((name) => !existingManagerNames.has(name));
  }, [allUsers, existingManagerNames, selectedTeamName]);

  useEffect(() => {
    if (!isAddModalOpen) return;
    if (!teamNames.includes(selectedTeamName)) {
      setSelectedTeamName(teamNames[0] || '');
    }
  }, [isAddModalOpen, selectedTeamName, teamNames]);

  useEffect(() => {
    if (!isAddModalOpen) return;
    if (!availableManagersByTeam.length) {
      setSelectedManagerName('');
      return;
    }
    if (!availableManagersByTeam.includes(selectedManagerName)) {
      setSelectedManagerName(availableManagersByTeam[0]);
    }
  }, [availableManagersByTeam, isAddModalOpen, selectedManagerName]);

  const monthlyTotals = useMemo(() => {
    return Array.from({ length: 12 }, (_, monthIdx) =>
      rows.reduce((sum, row) => sum + (Number(row.months?.[monthIdx]) || 0), 0)
    );
  }, [rows]);

  const yearTotal = useMemo(() => {
    return monthlyTotals.reduce((acc, cur) => acc + cur, 0);
  }, [monthlyTotals]);

  const formatMoney = (amount: number) => {
    if (amount === 0) return '-';
    return Math.floor(amount).toLocaleString();
  };

  const startEdit = (row: TargetRow) => {
    setEditingId(row.id);
    setEditMonths((prev) => ({
      ...prev,
      [row.id]: (row.months || []).map((m) => Number(m) || 0)
    }));
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const handleMonthChange = (rowId: number, idx: number, val: string) => {
    const num = Number(val);
    setEditMonths((prev) => {
      const current = prev[rowId] ? [...prev[rowId]] : Array(12).fill(0);
      current[idx] = Number.isFinite(num) && num >= 0 ? num : 0;
      return { ...prev, [rowId]: current };
    });
  };

  const saveEdit = async (row: TargetRow) => {
    const months = editMonths[row.id] || row.months;
    setSavingId(row.id);
    try {
      const res = await crmDataService.updateSalesTarget(row.id, months);
      if (!res || res.status !== 'success') {
        throw new Error(res?.message || '저장 실패');
      }
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id
            ? { ...r, months, total: months.reduce((a, b) => a + (Number(b) || 0), 0) }
            : r
        )
      );
      setEditingId(null);
    } catch (err) {
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setSavingId(null);
    }
  };

  // ✨ [추가] 목표 이전 실행 핵심 로직 (제품/상품 개별 이관 방식)
  const handleTransferTarget = async () => {
    if (!transferFrom || !transferTo || transferFrom === transferTo) {
      alert("보내는 사람과 받는 사람을 정확히 선택해주세요.");
      return;
    }

    const transferEntries = Object.entries(transferAmounts).filter(([idx, amts]) => 
      selectedMonths.includes(Number(idx)) && ((amts.prod || 0) > 0 || (amts.item || 0) > 0)
    );
    if (transferEntries.length === 0) {
      alert("이전할 월을 선택하고 제품 혹은 상품 금액을 입력해주세요.");
      return;
    }

    if (!window.confirm("입력하신 금액만큼 항목별 목표를 이전하시겠습니까?")) return;

    setIsTransferring(true);
    try {
      const fromRows = rows.filter(r => r.manager === transferFrom);
      const toRows = rows.filter(r => r.manager === transferTo);

      // 제품('prod')과 상품('item')을 각각 루프 돌며 처리
      const categories = [
        { key: 'prod', label: '제품' },
        { key: 'item', label: '상품' }
      ];

      for (const cat of categories) {
        const fromRow = fromRows.find(r => r.type === cat.label);
        const toRow = toRows.find(r => r.type === cat.label);
        
        if (!fromRow || !toRow) continue;

        const newFromMonths = fromRow.months.map((m, idx) => {
          if (!selectedMonths.includes(idx)) return m;
          const moveAmt = transferAmounts[idx]?.[cat.key as 'prod' | 'item'] || 0;
          return Math.max(0, Math.round(m - moveAmt));
        });

        const newToMonths = toRow.months.map((m, idx) => {
          if (!selectedMonths.includes(idx)) return m;
          const moveAmt = transferAmounts[idx]?.[cat.key as 'prod' | 'item'] || 0;
          return Math.round(m + moveAmt);
        });

        // 실제 DB 업데이트 수행
        await crmDataService.updateSalesTarget(fromRow.id, newFromMonths);
        await crmDataService.updateSalesTarget(toRow.id, newToMonths);
      }

      alert("항목별 목표 이전이 완료되었습니다.");
      setIsTransferModalOpen(false);
      setReloadKey(k => k + 1); // 데이터 새로고침
    } catch (err) {
      alert("이전 중 오류가 발생했습니다.");
    } finally {
      setIsTransferring(false);
    }
  };

  const openAddModal = () => {
    setSelectedTeamName(teamNames[0] || '');
    setSelectedManagerName('');
    setIsAddModalOpen(true);
  };

  const handleAddManager = async () => {
    if (!selectedTeamName) {
      alert('팀을 먼저 선택해주세요.');
      return;
    }
    if (!selectedManagerName) {
      alert('담당자를 선택해주세요.');
      return;
    }

    setAddingManager(true);
    try {
      const res = await crmDataService.addSalesTargetManager(selectedManagerName, 2026);
      if (!res || res.status !== 'success') {
        throw new Error(res?.message || '담당자 추가 실패');
      }
      setIsAddModalOpen(false);
      setReloadKey((k) => k + 1);
    } catch (err) {
      alert('담당자 추가 중 오류가 발생했습니다.');
    } finally {
      setAddingManager(false);
    }
  };

  const handleDeleteManager = async (manager: string) => {
    if (!window.confirm(`'${manager}' 담당자의 목표 데이터를 삭제하시겠습니까?`)) return;
    setDeletingManager(manager);
    try {
      const res = await crmDataService.deleteSalesTargetManager(manager, 2026);
      if (!res || res.status !== 'success') {
        throw new Error(res?.message || '담당자 삭제 실패');
      }
      setRows((prev) => prev.filter((row) => row.manager !== manager));
      if (editingId !== null) {
        const isEditingManager = rows.some((row) => row.id === editingId && row.manager === manager);
        if (isEditingManager) setEditingId(null);
      }
    } catch (err) {
      alert('담당자 삭제 중 오류가 발생했습니다.');
    } finally {
      setDeletingManager(null);
    }
  };

  const handleExportExcel = () => {
    if (!rows.length) {
      alert('내보낼 데이터가 없습니다.');
      return;
    }

    const ok = window.confirm(
      `총 ${rows.length}건을 엑셀로 저장할까요?`
    );
    if (!ok) return;

    const excelRows: any[] = [];

    groupedRows.forEach(([manager, managerRows]) => {
      const subTotals = Array(12).fill(0);
      let grandTotal = 0;

      managerRows.forEach((row) => {
        excelRows.push({
          담당자: row.manager,
          구분: row.type,
          '1월': row.months[0] || 0,
          '2월': row.months[1] || 0,
          '3월': row.months[2] || 0,
          '4월': row.months[3] || 0,
          '5월': row.months[4] || 0,
          '6월': row.months[5] || 0,
          '7월': row.months[6] || 0,
          '8월': row.months[7] || 0,
          '9월': row.months[8] || 0,
          '10월': row.months[9] || 0,
          '11월': row.months[10] || 0,
          '12월': row.months[11] || 0,
          총합계: row.total || 0
        });

        row.months.forEach((m, idx) => {
          subTotals[idx] += (Number(m) || 0);
          grandTotal += (Number(m) || 0);
        });
      });

      excelRows.push({
        담당자: manager,
        구분: '소계',
        '1월': subTotals[0],
        '2월': subTotals[1],
        '3월': subTotals[2],
        '4월': subTotals[3],
        '5월': subTotals[4],
        '6월': subTotals[5],
        '7월': subTotals[6],
        '8월': subTotals[7],
        '9월': subTotals[8],
        '10월': subTotals[9],
        '11월': subTotals[10],
        '12월': subTotals[11],
        총합계: grandTotal
      });
    });

    const ws = XLSX.utils.json_to_sheet(excelRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SalesTarget');

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `sales_target_2026_${today}.xlsx`);
  };

  const handleUploadExcel = () => {
    setUploadFile(null);
    setIsUploadModalOpen(true);
  };

  const handleConfirmUpload = async () => {
    if (!uploadFile) {
      alert('엑셀 파일을 선택해주세요.');
      return;
    }

    const lower = uploadFile.name.toLowerCase();
    if (!(lower.endsWith('.xlsx') || lower.endsWith('.xls'))) {
      alert('엑셀 파일(.xlsx, .xls)만 업로드할 수 있습니다.');
      return;
    }

    setIsUploading(true);
    const result = await crmDataService.uploadExcelFile('sales_target', uploadFile);
    setIsUploading(false);

    if (result?.status === 'success') {
      alert(`업로드 완료: ${result.filename}`);
      setIsUploadModalOpen(false);
      setUploadFile(null);

      await crmDataService.getSalesTargets(true);
      setReloadKey((k) => k + 1);

      return;
    }

    alert(`업로드 실패: ${result?.message || '알 수 없는 오류'}`);
  };

  const handleFinalizeUpload = async () => {
    if (!window.confirm('가장 최신 버전 묶음을 병합해 최종본을 만들까요?')) return;
    setIsFinalizing(true);
    const result = await crmDataService.finalizeExcelUpload('sales_target');
    setIsFinalizing(false);
    if (result?.status === 'success') {
      alert(`최종 업로드 완료: ${result.filename}`);
      
      await crmDataService.getSalesTargets(true);
      setReloadKey((k) => k + 1);
    } else {
      alert(`최종 업로드 실패: ${result?.message || '알 수 없는 오류'}`);
    }
  };

  // 보내는 사람의 데이터만 요약해서 보여주는 헬퍼
  const fromUserData = useMemo(() => {
    return rows.filter(r => r.manager === transferFrom);
  }, [rows, transferFrom]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-xl font-bold text-slate-500 animate-pulse">
          매출 목표 데이터를 불러오는 중...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-slate-50 gap-4">
        <div className="text-red-500 font-bold text-lg">⚠️ {error}</div>
        <button 
          onClick={() => setReloadKey((k) => k + 1)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold"
        >
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-slate-50">
      {/* 상단 헤더 */}
      <div className="p-6 bg-white border-b border-slate-200 flex justify-between items-center shadow-sm z-10">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            2026년 매출 목표 (Target Summary)
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            담당자별 제품/상품 매출 목표 현황입니다. (최초 실행 시 GCS 자동 동기화)
          </p>
        </div>
        
        {/* 버튼 영역 */}
        <div className="flex gap-2">
          {/* ✨ [수정] 목표 이전 모달 열기 버튼: 🚨 PermissionGate 채우기 */}
          <PermissionGate user={currentUser} action="sales_target_edit">
            <button 
              onClick={() => { 
                setIsTransferModalOpen(true); 
                setTransferFrom(''); setTransferTo(''); setSelectedMonths([]); setTransferAmounts({}); 
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-[14px] font-black text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 transition-colors shadow-sm"
            >
              <ArrowRightLeft size={16} />
              목표 금액 이전
            </button>
          </PermissionGate>

          {/* 🚨 PermissionGate 채우기 (최종 업로드) */}
          <PermissionGate user={currentUser} action="sales_target_edit">
            <button
              onClick={handleFinalizeUpload}
              disabled={isFinalizing}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-[14px] font-black text-orange-700 bg-blue-50/50 border border-slate-300 hover:bg-orange-100 transition-colors shadow-sm disabled:opacity-50"
            >
              <Upload size={16} />
              {isFinalizing ? '최종 업로드 중...' : '최종 업로드'}
            </button>
          </PermissionGate>

          {/* 🚨 PermissionGate 채우기 (파일 업로드) */}
          <PermissionGate user={currentUser} action="sales_target_edit">
            <button
              onClick={handleUploadExcel}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-[14px] font-black text-blue-700 bg-blue-50/50 border border-slate-300 hover:bg-blue-100 transition-colors shadow-sm"
            >
              <Upload size={16} />
              파일 업로드
            </button>
          </PermissionGate>

          <button 
            onClick={handleExportExcel} 
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-[14px] font-black text-emerald-700 bg-blue-50/50 border border-slate-300 hover:bg-emerald-100 transition-all shadow-sm active:scale-95"
            >
              <FileSpreadsheet size={16} />
              엑셀 다운로드
          </button>
        </div>
      </div>

      {/* 메인 테이블 영역 */}
      <div className="flex-1 overflow-auto p-1">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden w-full">
          <table className="w-full text-sm text-right border-collapse">
            <thead className="bg-slate-100 text-slate-600 font-bold text-xs uppercase tracking-wider sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-3 py-3 text-center border-b border-r border-slate-200 sticky left-0 bg-slate-100 z-20 min-w-[140px]">담당자</th>
                <th className="px-2 py-3 text-center border-b border-r border-slate-200 min-w-[80px]">구분</th>
                {[...Array(12)].map((_, i) => (
                  <th key={i} className="px-2 py-3 text-center border-b border-r border-slate-200 min-w-[95px]">
                    {i + 1}월
                  </th>
                ))}
                <th className="px-3 py-3 text-center border-b border-slate-200 bg-slate-200 min-w-[120px]">총합계</th>
                <th className="px-3 py-3 text-center border-b border-slate-200 min-w-[110px]">관리</th>
              </tr>
            </thead>

            {/* 매출목표 담당자별 디자인부분 */}
            <tbody className="divide-y divide-slate-200">
              {groupedRows.map(([manager, managerRows]) => {
                const managerSubtotals = Array(12).fill(0);
                let managerGrandTotal = 0;

                managerRows.forEach(row => {
                  const isEditing = editingId === row.id;
                  const currentMonths = isEditing ? (editMonths[row.id] || row.months) : row.months;
                  
                  currentMonths.forEach((val, idx) => {
                    const num = Number(val) || 0;
                    managerSubtotals[idx] += num;
                    managerGrandTotal += num;
                  });
                });

                return (
                  <React.Fragment key={manager}>
                    {managerRows.map((row, rowIdx) => {
                      const isEditing = editingId === row.id;
                      return (
                        <tr key={row.id} className="hover:bg-blue-50/30 transition-colors group">
                          {rowIdx === 0 && (
                            <td
                              rowSpan={managerRows.length + 1}
                              className="px-3 py-2 text-center font-black text-slate-800 border-r border-b-2 border-slate-300 sticky left-0 bg-white z-10 align-middle shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)]"
                            >
                              <div className="flex items-center justify-center gap-1.5">
                                <span>{manager}</span>
                                {/* 🚨 [유지/수정] PermissionGate user={currentUser} 로 명찰 변경 */}
                                <PermissionGate user={currentUser} action="sales_target_edit">
                                    <button
                                    onClick={() => handleDeleteManager(manager)}
                                    className="p-1 text-slate-300 hover:text-red-500 transition-all disabled:opacity-50"
                                    title="담당자 삭제"
                                    disabled={editingId !== null || deletingManager === manager}
                                    >
                                    <Trash2 size={13} />
                                    </button>
                                </PermissionGate>
                              </div>
                            </td>
                          )}

                          <td className={`px-2 py-2 text-center border-r border-slate-100 font-bold text-xs ${row.type === '제품' ? 'text-blue-600' : 'text-emerald-600'}`}>
                            {row.type}
                          </td>

                          {row.months.map((amount, idx) => {
                            const editVal = editMonths[row.id]?.[idx] ?? 0;
                            return (
                              <td key={idx} className={`p-0 border-r border-slate-100 transition-all ${isEditing ? 'bg-blue-50' : ''}`}>
                                {isEditing ? (
                                  <div className="flex items-center h-full w-full px-1">
                                    <input
                                      type="number"
                                      autoFocus={idx === 0}
                                      className="w-full text-right bg-white border border-blue-400 rounded-sm px-1 py-0.5 text-[11px] font-semibold text-blue-700 outline-none shadow-inner"
                                      value={editVal}
                                      onChange={(e) => handleMonthChange(row.id, idx, e.target.value)}
                                    />
                                  </div>
                                ) : (
                                  <div className="px-2 py-2">
                                    <span className={`block text-right text-[11px] transition-all ${amount > 0 ? 'font-semibold text-slate-700' : 'font-medium text-slate-300'}`}>
                                      {amount > 0 ? formatMoney(amount) : '-'}
                                    </span>
                                  </div>
                                )}
                              </td>
                            );
                          })}

                          <td className="px-3 py-2 font-bold text-slate-800 bg-slate-50 border-l border-slate-200 text-right">
                            {formatMoney(
                              isEditing
                                ? (editMonths[row.id] || row.months).reduce((a, b) => a + (Number(b) || 0), 0)
                                : row.total
                            )}
                          </td>
                          <td className="px-3 py-2 text-center border-l border-slate-200">
                            {isEditing ? (
                              <div className="flex justify-center gap-1">
                                <button
                                  onClick={() => saveEdit(row)}
                                  className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-md disabled:opacity-50"
                                  title="저장"
                                  disabled={savingId === row.id}
                                >
                                  <Check size={14} />
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="p-1 bg-slate-200 text-slate-600 rounded hover:bg-slate-300"
                                  title="취소"
                                  disabled={savingId === row.id}
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ) : (
                              /* 🚨 [유지/수정] PermissionGate user={currentUser} 로 명찰 변경 */
                              <PermissionGate user={currentUser} action="sales_target_edit">
                                <div className="flex justify-center gap-1">
                                    <button
                                    onClick={() => startEdit(row)}
                                    className="p-1.5 text-slate-400 hover:text-blue-600 transition-all disabled:opacity-50"
                                    title="수정"
                                    disabled={editingId !== null}
                                    >
                                    <Edit2 size={14} />
                                    </button>
                                </div>
                              </PermissionGate>
                            )}
                          </td>
                        </tr>
                      );
                    })}

                    <tr className="bg-slate-100/80 hover:bg-slate-200/50 transition-colors">
                      <td className="px-2 py-2 text-center border-r border-b-2 border-slate-300 font-black text-xs text-slate-600">
                        소계
                      </td>
                      {managerSubtotals.map((sum, idx) => (
                        <td key={idx} className="px-2 py-2 text-right border-r border-b-2 border-slate-300 font-bold text-slate-700 text-[11px]">
                          {sum > 0 ? formatMoney(sum) : '-'}
                        </td>
                      ))}
                      <td className="px-3 py-2 font-black text-slate-900 bg-slate-200/50 border-l border-b-2 border-slate-300 text-right shadow-inner">
                        {formatMoney(managerGrandTotal)}
                      </td>
                      <td className="px-3 py-2 text-center border-l border-b-2 border-slate-300">
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
              
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={16} className="py-12 text-center text-slate-400 font-bold">
                    표시할 매출 목표 데이터가 DB에 없습니다.
                  </td>
                </tr>
              )}

              <tr className="bg-slate-50 border-t border-slate-200 shadow-sm">
                <td className="px-3 py-3 text-center border-r border-slate-200 sticky left-0 bg-slate-50 z-10">
                  {/* 🚨 [유지/수정] PermissionGate user={currentUser} 로 명찰 변경 */}
                  <PermissionGate user={currentUser} action="sales_target_edit">
                    <button
                        onClick={openAddModal}
                        disabled={editingId !== null}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 disabled:opacity-50"
                    >
                        <Plus size={14} />
                        담당자 추가
                    </button>
                  </PermissionGate>
                </td>
                <td className="px-2 py-3 text-center border-r border-slate-200 font-bold text-slate-700">월별 합계</td>
                {monthlyTotals.map((sum, idx) => (
                  <td key={idx} className="px-2 py-3 text-right border-r border-slate-200 font-black text-slate-800 text-xs">
                    {formatMoney(sum)}
                  </td>
                ))}
                <td className="px-3 py-3 font-black text-blue-700 bg-blue-50 border-l border-slate-200 text-right text-sm">
                  {formatMoney(yearTotal)}
                </td>
                <td className="px-3 py-3 text-center text-slate-400">-</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ✨ [수정] 목표 이전 모달: 제품/상품 개별 금액 입력 방식 */}
      {isTransferModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center animate-fade-in p-4">
          <div className="w-full max-w-[650px] bg-white rounded-[32px] shadow-2xl p-8 border border-slate-100 overflow-y-auto max-h-[90vh]">
            <div className="flex items-center gap-3 mb-6 border-l-4 border-indigo-600 pl-4">
              <ArrowRightLeft className="text-indigo-600" size={24} />
              <h3 className="text-xl font-black text-slate-800">목표 이관 (제품/상품 개별 입력)</h3>
            </div>

            <div className="space-y-6">
              {/* 1. 담당자 선택 구역 */}
              <div className="grid grid-cols-11 items-center gap-2">
                <div className="col-span-5">
                  <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">보내는 사람</label>
                  <select 
                    value={transferFrom} 
                    onChange={(e) => { setTransferFrom(e.target.value); setSelectedMonths([]); setTransferAmounts({}); }}
                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">담당자 선택</option>
                    {Array.from(existingManagerNames).map(name => <option key={name} value={name}>{name}</option>)}
                  </select>
                </div>
                <div className="col-span-1 flex justify-center mt-4"><MoveRight className="text-slate-300" size={20} /></div>
                <div className="col-span-5">
                  <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">받는 사람</label>
                  <select 
                    value={transferTo} 
                    onChange={(e) => setTransferTo(e.target.value)}
                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">담당자 선택</option>
                    {Array.from(existingManagerNames).map(name => <option key={name} value={name}>{name}</option>)}
                  </select>
                </div>
              </div>

              {/* 🗓️ 2. 월 선택 섹션 (기본값 미선택) */}
              {transferFrom && (
                <div className="bg-slate-50 p-5 rounded-2xl space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="text-[10px] font-black text-slate-400 uppercase block">이전할 월 선택</label>
                    <button 
                      onClick={() => {
                        const all = Array.from({length: 12}, (_, i) => i);
                        setSelectedMonths(selectedMonths.length === 12 ? [] : all);
                      }}
                      className="text-[10px] font-bold text-indigo-600 hover:underline"
                    >
                      {selectedMonths.length === 12 ? '전체 해제' : '전체 선택'}
                    </button>
                  </div>
                  <div className="grid grid-cols-6 gap-2">
                    {[...Array(12)].map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedMonths(prev => prev.includes(i) ? prev.filter(m => m !== i) : [...prev, i])}
                        className={`py-2 rounded-xl text-xs font-black transition-all border ${selectedMonths.includes(i) ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-200 text-slate-400'}`}
                      >
                        {i + 1}월
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* 💰 3. 제품/상품 개별 금액 입력 구역 */}
              {selectedMonths.length > 0 && (
                <div className="bg-slate-50 p-5 rounded-2xl space-y-4 border border-slate-100">
                  <label className="text-[10px] font-black text-slate-400 uppercase block">월별 제품/상품 이전 금액 입력</label>
                  <div className="space-y-3 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
                    {selectedMonths.sort((a,b)=>a-b).map(idx => {
                      // 보내는 사람의 제품/상품 현재 목표액을 각각 가져옴
                      const prodMax = fromUserData.find(r => r.type === '제품')?.months[idx] || 0;
                      const itemMax = fromUserData.find(r => r.type === '상품')?.months[idx] || 0;

                      return (
                        <div key={idx} className="bg-white p-4 rounded-2xl border border-slate-200 space-y-3 shadow-sm">
                          <div className="text-xs font-black text-indigo-600 pb-2 border-b border-slate-50">{idx + 1}월 이관 설정</div>
                          <div className="grid grid-cols-2 gap-4">
                            {/* 제품 입력 */}
                            <div>
                              <div className="text-[13px] font-bold text-blue-600 mb-1 flex justify-between">
                                <span>제품</span>
                                <span>(보유: {prodMax.toLocaleString()})</span>
                              </div>
                              <input 
                                type="number" 
                                placeholder="금액 입력" 
                                value={transferAmounts[idx]?.prod || ''} 
                                onChange={(e) => {
                                  const val = Math.min(prodMax, Number(e.target.value));
                                  setTransferAmounts({
                                    ...transferAmounts, 
                                    [idx]: { ...(transferAmounts[idx] || {item: 0}), prod: val }
                                  });
                                }} 
                                className="w-full p-2 text-right bg-blue-50/30 border border-blue-100 rounded-lg text-xs font-bold text-blue-700 focus:ring-2 focus:ring-blue-400 outline-none" 
                              />
                            </div>
                            {/* 상품 입력 */}
                            <div>
                              <div className="text-[13px] font-bold text-emerald-600 mb-1 flex justify-between">
                                <span>상품</span>
                                <span>(보유: {itemMax.toLocaleString()})</span>
                              </div>
                              <input 
                                type="number" 
                                placeholder="금액 입력" 
                                value={transferAmounts[idx]?.item || ''} 
                                onChange={(e) => {
                                  const val = Math.min(itemMax, Number(e.target.value));
                                  setTransferAmounts({
                                    ...transferAmounts, 
                                    [idx]: { ...(transferAmounts[idx] || {prod: 0}), item: val }
                                  });
                                }} 
                                className="w-full p-2 text-right bg-emerald-50/30 border border-emerald-100 rounded-lg text-xs font-bold text-emerald-700 focus:ring-2 focus:ring-emerald-400 outline-none" 
                              />
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-8 flex gap-3">
              <button 
                onClick={() => {
                  setIsTransferModalOpen(false);
                  setTransferFrom(''); setTransferTo(''); setSelectedMonths([]); setTransferAmounts({});
                }} 
                className="flex-1 py-4 rounded-2xl bg-slate-100 text-slate-500 font-bold hover:bg-slate-200 transition-all"
              >
                취소
              </button>
              <button 
                onClick={handleTransferTarget} 
                disabled={isTransferring || !transferFrom || !transferTo || selectedMonths.length === 0}
                className="flex-1 py-4 rounded-2xl bg-indigo-600 text-white font-black hover:bg-indigo-700 disabled:opacity-50 shadow-lg flex items-center justify-center gap-2"
              >
                {isTransferring ? <RefreshCw size={18} className="animate-spin" /> : <Check size={18} />}
                이전 실행
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 담당자 추가/업로드 모달 (기존 원본 코드 유지) */}
      {isAddModalOpen && ( <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"><div className="w-[420px] bg-white rounded-2xl shadow-2xl border border-slate-200 p-6"><h3 className="text-lg font-bold text-slate-800 mb-4">담당자 추가</h3><div className="space-y-4"><div><label className="text-[11px] font-bold text-slate-500">팀 선택</label><select value={selectedTeamName} onChange={(e) => setSelectedTeamName(e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm">{teamNames.map((t) => <option key={t} value={t}>{t}</option>)}</select></div><div><label className="text-[11px] font-bold text-slate-500">담당자 선택</label><select value={selectedManagerName} onChange={(e) => setSelectedManagerName(e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm" disabled={availableManagersByTeam.length === 0}><option value="">담당자 선택</option>{availableManagersByTeam.map((name) => <option key={name} value={name}>{name}</option>)}</select></div></div><div className="mt-6 flex justify-end gap-2"><button onClick={() => setIsAddModalOpen(false)} className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-bold hover:bg-slate-200">취소</button><button onClick={handleAddManager} disabled={addingManager || !selectedManagerName} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700">추가</button></div></div></div> )}
      {isUploadModalOpen && ( <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"><div className="w-[460px] bg-white rounded-2xl shadow-2xl border border-slate-200 p-6"><h3 className="text-lg font-bold text-slate-800 mb-4">엑셀 파일 업로드</h3><input type="file" accept=".xlsx,.xls" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} className="w-full text-sm text-slate-700 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border file:border-slate-300 file:bg-slate-50 file:font-bold" /><div className="mt-6 flex justify-end gap-2"><button onClick={() => setIsUploadModalOpen(false)} className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-bold hover:bg-slate-200">취소</button><button onClick={handleConfirmUpload} disabled={isUploading} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700">{isUploading ? '업로드 중...' : '추가하기'}</button></div></div></div> )}
    </div>
  );
};

export default SalesTarget;